"use client";

import { useState, useRef, useEffect, useContext } from "react";
import { usePathname } from "next/navigation";
import { FaBell, FaUserCircle, FaCog, FaSignOutAlt } from "react-icons/fa";
import Link from "next/link";
import { auth } from "@/components/context/firebase";
import { useRouter } from "next/navigation";
import { useAuthState } from "react-firebase-hooks/auth";
import { AppStateContext } from "./context/AppContext";
import { Bell, Crown, FileText, Play } from "lucide-react";
import { Button } from "./ui/button";
import Image from "next/image";
import AppTooltip from "./Tooltip";
import fetchWithAuth from "@/hooks/fetchWithAuth";
import { API_URL } from "@/components/config/config";
import { clearProfileCache } from "@/hooks/useFetchUserDetails";
import { clearAllCache } from "@/lib/dataCache";
import { mpReset } from "@/lib/analytics/mixpanel";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/language-switcher";
import { formatTimeAgo } from "@/lib/i18n/format";

// GET /recruiter-notifications/ (backend recruiter_notification_views.py).
type RecruiterNotification = {
  id: number;
  kind: string;
  stage: string;
  title: string;
  body: string;
  link: string;
  is_read: boolean;
  updated_at?: string;
};

export function TopBar() {
  const { t } = useTranslation();
  const pathname = usePathname();
  const [user] = useAuthState(auth);
  const { setUserProfile, userProfile, setIsUserFetching } = useContext(AppStateContext);

  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const [recruiterNotifications, setRecruiterNotifications] = useState<RecruiterNotification[]>([]);
  const [recruiterUnreadCount, setRecruiterUnreadCount] = useState(0);
  const [isRecruiterNotifOpen, setIsRecruiterNotifOpen] = useState(false);
  const recruiterNotifRef = useRef<HTMLDivElement>(null);
  const isRecruiterDashboard = pathname.startsWith("/dashboard/agency");

  // New resumes on a role and each interview's progress, from the backend
  // (recruiter_notifications.py). Polled every 30 seconds while the tab is
  // visible, and again on every page change and when the panel opens.
  function fetchRecruiterNotifications() {
    if (!userProfile?.uid) return;
    fetchWithAuth(`${API_URL}/recruiter-notifications/`, { cache: "no-store" })
      .then((res) => {
        if (!res.ok) throw new Error(`Recruiter notifications failed: ${res.status}`);
        return res.json();
      })
      .then((result) => {
        if (Array.isArray(result?.data)) setRecruiterNotifications(result.data);
        setRecruiterUnreadCount(Number(result?.unread_count || 0));
      })
      .catch((err) => {
        console.error("Failed to fetch recruiter notifications:", err);
      });
  }

  // Reads the list again once the write is done: the page change after a
  // click fetches it too, and that fetch can reach the server first.
  function markRecruiterNotificationsRead(id?: number) {
    const url = id
      ? `${API_URL}/recruiter-notifications/${id}/`
      : `${API_URL}/recruiter-notifications/`;
    fetchWithAuth(url, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ is_read: true }),
    })
      .catch((err) => {
        console.error("Failed to mark recruiter notification read:", err);
      })
      .finally(fetchRecruiterNotifications);
  }

  const handleRecruiterNotificationClick = (note: RecruiterNotification) => {
    setIsRecruiterNotifOpen(false);
    if (!note.is_read) {
      markRecruiterNotificationsRead(note.id);
      setRecruiterNotifications((prev) =>
        prev.map((item) => (item.id === note.id ? { ...item, is_read: true } : item))
      );
      setRecruiterUnreadCount((prev) => Math.max(0, prev - 1));
    }
    router.push(note.link);
  };

  const handleMarkAllRead = () => {
    markRecruiterNotificationsRead();
    setRecruiterNotifications((prev) => prev.map((item) => ({ ...item, is_read: true })));
    setRecruiterUnreadCount(0);
  };

  useEffect(() => {
    if (userProfile?.uid && userProfile?.role === "hr" && isRecruiterDashboard) {
      fetchRecruiterNotifications();
    }
  }, [userProfile?.uid, userProfile?.role, pathname])

  useEffect(() => {
    if (userProfile?.role !== "hr" || !userProfile?.uid || !isRecruiterDashboard) return;
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") fetchRecruiterNotifications();
    }, 30000);
    return () => clearInterval(interval);
  }, [userProfile?.uid, userProfile?.role, isRecruiterDashboard]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (recruiterNotifRef.current && !recruiterNotifRef.current.contains(event.target as Node)) {
        setIsRecruiterNotifOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const handleClick = (url) => {
    setIsProfileOpen(false);
    router.push(url);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    try {
      setIsUserFetching(true)
      auth
        .signOut()
        .then(() => {
        })
        .catch((error) => {
          console.error("Error signing out:", error);
        });
    } finally {
      clearProfileCache()
      clearAllCache()
      mpReset() // clear Mixpanel identity so the next user does not merge
      setUserProfile(null)
      window.location.href = "/"
    }
  };

  return (
    <header className="elevated-glass sticky top-0 z-20 flex h-16 items-center justify-between gap-2 sm:gap-4 border-b border-border/30 bg-card/60 px-6">
      <div className="flex items-center justify-end gap-3 w-full">
        {isRecruiterDashboard && <LanguageSwitcher />}
        {isRecruiterDashboard && userProfile?.role === "hr" && (
          <div className="relative" ref={recruiterNotifRef}>
            <button
              className="topbar-bell relative"
              title={t("Notifications")}
              onClick={() => {
                if (!isRecruiterNotifOpen) fetchRecruiterNotifications();
                setIsRecruiterNotifOpen((prev) => !prev);
              }}
            >
              <Bell className="h-5 w-5" />
              {recruiterUnreadCount > 0 && (
                <span className="topbar-badge absolute -top-2 -right-2 bg-red-700 text-white rounded-full min-w-5 h-5 px-1 text-xs flex items-center justify-center">
                  {recruiterUnreadCount > 99 ? "99+" : recruiterUnreadCount}
                </span>
              )}
            </button>

            {isRecruiterNotifOpen && (
              <div className="topbar-pop absolute right-0 mt-2 w-[420px] max-h-[520px] overflow-y-auto rounded-xl border border-border bg-card shadow-lg z-50">
                <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                  <p className="text-sm font-semibold">{t("Notifications")}</p>
                  {recruiterUnreadCount > 0 && (
                    <button
                      type="button"
                      onClick={handleMarkAllRead}
                      className="text-xs font-medium text-primary hover:underline"
                    >
                      {t("Mark all as read")}
                    </button>
                  )}
                </div>
                <div className="p-2">
                  {recruiterNotifications.length === 0 ? (
                    <p className="text-sm text-muted-foreground px-3 py-4">
                      {t("New resumes, interviews and reports on your roles show up here.")}
                    </p>
                  ) : (
                    recruiterNotifications.map((note) => (
                      <button
                        key={note.id}
                        onClick={() => handleRecruiterNotificationClick(note)}
                        className="w-full text-left px-3 py-3 rounded-lg hover:bg-accent transition-colors border border-transparent hover:border-border"
                      >
                        <p className="text-sm font-medium text-foreground">{note.title}</p>
                        {note.body && (
                          <p className="text-xs text-muted-foreground mt-1">{note.body}</p>
                        )}
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-[11px] text-muted-foreground">{formatTimeAgo(note.updated_at)}</span>
                          {!note.is_read && (
                            <span className="text-[11px] font-semibold text-primary">{t("New", { context: "notification" })}</span>
                          )}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center">
          <div className="relative" ref={dropdownRef}>
            <button onClick={() => setIsProfileOpen(!isProfileOpen)} className="flex items-center gap-2 text-foreground hover:bg-accent rounded-full transition-colors mr-2" title={t("Profile")}>
              <div className="topbar-avatar h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center">{user?.photoURL ? <Image src={user?.photoURL} alt={t("user")} width={32} height={32} className="w-full h-full rounded-full object-cover" /> : <FaUserCircle size={20} />}</div>
            </button>

            {isProfileOpen && (
              <div className="topbar-pop absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-card border border-border z-50">
                <div className="py-1">
                  <div className="px-4 py-2 border-b">
                    <p className="text-sm font-medium">{user?.displayName || t("User")}</p>
                    <p className="text-xs text-muted-foreground break-words">{userProfile?.user_email}</p>
                  </div>

                  <button
                    onClick={() => handleClick("/dashboard/agency/profile")}
                    className="flex items-center px-4 py-2 text-sm text-foreground hover:bg-accent w-full text-left"
                  >
                    <FaUserCircle className="mr-2 h-4 w-4" />
                    <span>{t("Profile")}</span>
                  </button>

                  <div className="border-t my-1"></div>
                  <button onClick={handleLogout} className="w-full flex items-center px-4 py-2 text-sm text-foreground hover:bg-accent">
                    <FaSignOutAlt className="mr-2 h-4 w-4" />
                    <span>{t("Logout")}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
